import styles from './DeliveryPurchaseModal.module.css';

import type { FC } from 'react';
import type { ApiListingItem } from 'src/types/listing';

import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { Truck } from '@phosphor-icons/react';

import { getApiErrorMessage } from 'src/utils/listingAvailability';
import { resolveDeliveryPointsNotice } from 'src/utils/deliveryPoints';

import { useDeliveryPoints, useCreateDeliveryOffer } from 'src/queries';

import { Modal } from 'src/components/Modal';
import { Stack } from 'src/components/themed/Stack';
import { Typography } from 'src/components/themed/Typography';

interface DeliveryPurchaseModalProps {
  visible: boolean;
  onClose: () => void;
  listing: ApiListingItem;
  /**
   * The delivery point to start with (api#425: the city the assistant's "Prepare the offer" carries).
   * Taken once the list of cities is here and only when it holds the point; the select stays the
   * person's to change.
   */
  initialPointId?: string;
}

// Mirror the api validation (create-delivery-offer-payload.dto): full name 2..120,
// phone 5..32, tax id 4..64; format is not validated server-side — tax numbers differ by country
// and a client-side pattern would refuse valid ones.
const NAME_MIN = 2;
const NAME_MAX = 120;
const PHONE_MIN = 5;
const PHONE_MAX = 32;
const TAX_ID_MIN = 4;
const TAX_ID_MAX = 64;

/**
 * "Buy with Delivery" master (deal v2, spec §8 PR-7): pick a delivery city, enter the
 * recipient's name + phone (frozen into the deal like a plane ticket, spec §5), then
 * create the offer. The offer is free and withdrawable; the exact preliminary invoice
 * (price + fee + gas, delivery quoted by the dealer) appears on the deal screen the
 * buyer lands on — the api materializes those snapshots only on offer creation (§4.10).
 */
export const DeliveryPurchaseModal: FC<DeliveryPurchaseModalProps> = ({
  visible,
  onClose,
  listing,
  initialPointId,
}) => {
  const navigate = useNavigate();
  const {
    data: pointsData,
    isLoading: pointsLoading,
    error: pointsError,
  } = useDeliveryPoints({ enabled: visible });
  const createOffer = useCreateDeliveryOffer();

  // The city the assistant's address named starts selected (api#425); the select is the person's from then on.
  const [cityId, setCityId] = useState(initialPointId ?? '');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [taxId, setTaxId] = useState('');
  const [error, setError] = useState('');

  const points = pointsData?.items ?? [];
  // A city is chosen only when the list holds it: an id the address named and the list does not
  // know shows "Select a city" and cannot be sent — the api would refuse it anyway.
  const cityKnown = points.some((point) => point.id === cityId);
  // Without this the 403 an unlinked wallet gets left a dead, empty "Select a city" list with no
  // explanation (found in the 12.08 live run).
  const pointsNotice = resolveDeliveryPointsNotice({
    isLoading: pointsLoading,
    error: pointsError,
    count: points.length,
  });
  const trimmedName = fullName.trim();
  const trimmedPhone = phone.trim();
  const trimmedTaxId = taxId.trim();
  const nameValid = trimmedName.length >= NAME_MIN && trimmedName.length <= NAME_MAX;
  const phoneValid = trimmedPhone.length >= PHONE_MIN && trimmedPhone.length <= PHONE_MAX;
  const taxIdValid = trimmedTaxId.length >= TAX_ID_MIN && trimmedTaxId.length <= TAX_ID_MAX;
  const canSubmit = cityKnown && nameValid && phoneValid && taxIdValid && !createOffer.isPending;

  const handleClose = () => {
    if (createOffer.isPending) {
      return;
    }
    setError('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    setError('');

    try {
      const deal = await createOffer.mutateAsync({
        listingId: listing.id,
        deliveryPointId: cityId,
        recipientFullName: trimmedName,
        recipientPhone: trimmedPhone,
        recipientTaxId: trimmedTaxId,
      });
      onClose();
      navigate(`/delivery/${deal.id}`, { replace: true });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not send the offer. Please try again.'));
    }
  };

  return (
    <Modal
      visible={visible}
      onClose={handleClose}
      icon={<Truck size={64} color="white" weight="fill" />}
      header={`Buy with Delivery: ${listing.make.name} ${listing.model.name}`}
      buttonText={createOffer.isPending ? 'Sending…' : 'Send offer'}
      onSubmit={handleSubmit}
      disabled={!canSubmit}
    >
      <Stack gap={3} width="100%">
        <Stack gap={1} className={styles.field}>
          <Typography color="lightWhite" variant="small">
            Delivery city
          </Typography>
          <select
            className={styles.select}
            value={cityKnown ? cityId : ''}
            onChange={(e) => setCityId(e.target.value)}
            disabled={pointsLoading || !!pointsNotice}
          >
            <option value="">
              {pointsLoading ? 'Loading cities…' : pointsNotice ? 'Unavailable' : 'Select a city'}
            </option>
            {points.map((point) => (
              <option key={point.id} value={point.id}>
                {point.city}
              </option>
            ))}
          </select>
          {pointsNotice ? (
            <Typography color="white" variant="small" className={styles.error}>
              {pointsNotice}
            </Typography>
          ) : null}
        </Stack>

        <Stack gap={1} className={styles.field}>
          <Typography color="lightWhite" variant="small">
            Recipient full name (as in the document)
          </Typography>
          <input
            type="text"
            className={styles.input}
            placeholder="Full name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            maxLength={NAME_MAX}
          />
        </Stack>

        <Stack gap={1} className={styles.field}>
          <Typography color="lightWhite" variant="small">
            Recipient phone
          </Typography>
          <input
            type="tel"
            inputMode="tel"
            className={styles.input}
            placeholder="+380..."
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            maxLength={PHONE_MAX}
          />
        </Stack>

        <Stack gap={1} className={styles.field}>
          <Typography color="lightWhite" variant="small">
            Tax number
          </Typography>
          <input
            type="text"
            className={styles.input}
            placeholder="VAT / tax identification number"
            value={taxId}
            onChange={(e) => setTaxId(e.target.value)}
            maxLength={TAX_ID_MAX}
          />
          <Typography color="lightWhite" variant="small">
            The dealer needs it to invoice this sale. Only the dealer sees it.
          </Typography>
        </Stack>

        <Stack gap={1} className={styles.note}>
          <Typography color="white" variant="small" fontWeight="600">
            No payment now
          </Typography>
          <Typography color="lightWhite" variant="small">
            You will pay the car price, the delivery and a platform fee only after the dealer
            confirms. Delivery is quoted by the dealer on approval. You can withdraw the offer any
            time before paying.
          </Typography>
        </Stack>

        {error ? (
          <Typography color="white" variant="small" className={styles.error}>
            {error}
          </Typography>
        ) : null}
      </Stack>
    </Modal>
  );
};
